// src/pages/News/AllNews.tsx

import {
  AddTwoTone,
  AnnouncementTwoTone,
  HighlightOffTwoTone,
  InfoTwoTone,
  MoreVertTwoTone,
  SearchTwoTone,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Collapse,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { NewsPool } from '@/types/domain/news'

import { ASC, DESC, getStorage, isFilterMatch, setStorage } from '../../api'
import {
  AM,
  AMItem,
  DlgNewsPoolInfo,
  HStack,
  KPage,
  KTextField,
  NewsCategorySelector,
  NewsSortSelector,
  NewsTile,
} from '../../components'
import { languageConstants as LC } from '../../constants'
import { useNewsPool } from '../../hooks'
import useRenderCount from '../../hooks/tools/useRenderCount'

type AllNewsProps = {
  PAGE_CONFIG?: string
  serviceName?: string
  URL_NEWS_NEW?: string
}

export const AllNews = ({
  PAGE_CONFIG = 'settings_news_all',
  serviceName = 'News',
  URL_NEWS_NEW = '/news/new',
}: AllNewsProps) => {
  const rc = useRenderCount()
  console.log('AllNews', rc)

  const navigate = useNavigate()

  const nPool: NewsPool = useNewsPool()

  // config

  const [actionMenu, setActionMenu] = useState(null)
  const [config, setConfig] = useState(getStorage(PAGE_CONFIG))
  useEffect(() => setStorage(PAGE_CONFIG, config), [config, PAGE_CONFIG])
  const { category, filter, order, orderby, poolInfo, type } = config

  // filter

  const inputFilter = f =>
    filter
      ? isFilterMatch(filter, f.attributes.title) ||
        isFilterMatch(filter, f.attributes.headline) ||
        isFilterMatch(filter, f.attributes.content_text) ||
        Number(filter) === Number(f.id)
      : f

  const categoryFilter = f =>
    category === LC.NEWSCATEGORY_CONTRACT
      ? f.attributes.contract_id !== null
      : category === LC.NEWSCATEGORY_OBJECT
        ? f.attributes.folder_id !== null
        : f

  const typeFilter = f =>
    type === LC.NEWSTYPE_TECH
      ? f.attributes.news_type_id === 1
      : type === LC.NEWSTYPE_LEGAL
        ? f.attributes.news_type_id === 2
        : type === LC.NEWSTYPE_BUSINESS
          ? f.attributes.news_type_id === 3
          : f

  const sortFilter = (a, b) => {
    if (orderby === 'title' && a.attributes[orderby] < b.attributes[orderby])
      return order === DESC ? 1 : -1

    if (orderby === 'entity_id' && Number(a.id) < Number(b.id))
      return order === DESC ? 1 : -1

    return order === DESC ? -1 : 1
  }

  const changeOrderByFilter = (event, orderby) => {
    if (orderby !== null) {
      setConfig(prev => ({ ...prev, orderby }))
    } else {
      const newOrder = order === DESC ? ASC : DESC
      setConfig(prev => ({ ...prev, order: newOrder }))
    }
  }

  const changeCategoryFilter = (event, status) => {
    if (status !== null) setConfig(prev => ({ ...prev, status }))
  }

  const amData = [
    {
      caption: 'Neu',
      icon: <AddTwoTone />,
      fn: () => navigate(URL_NEWS_NEW),
    },
    {
      caption: 'Info',
      icon: <InfoTwoTone />,
      fn: () => setConfig(prev => ({ ...prev, poolInfo: true })),
    },
  ]

  return (
    <>
      <AM
        anchorEl={actionMenu}
        caption={serviceName}
        onClose={() => setActionMenu(null)}
        open={Boolean(actionMenu)}
        historyUrlTarget={'dashboard'}
      >
        {amData.map(am => (
          <AMItem
            caption={am.caption}
            icon={am.icon}
            key={am.caption}
            onClick={am.fn}
          />
        ))}
      </AM>

      <DlgNewsPoolInfo
        open={poolInfo}
        onClose={() => setConfig(prev => ({ ...prev, poolInfo: false }))}
      />

      <KPage
        action={
          <IconButton onClick={event => setActionMenu(event.currentTarget)}>
            <MoreVertTwoTone />
          </IconButton>
        }
        avatar={
          <Avatar>
            <AnnouncementTwoTone />
          </Avatar>
        }
        title={serviceName}
      >
        <Collapse in={nPool.length === 0}>
          <LinearProgress sx={{ m: 1 }} />
        </Collapse>

        <Collapse in={nPool.length > 0}>
          <HStack justifyContent={'space-between'} sx={{ mx: 1, mb: 1 }}>
            <HStack>
              <Stack
                alignItems={'center'}
                direction={{ lg: 'column', xl: 'row' }}
                spacing={1}
              >
                <NewsCategorySelector
                  onChange={changeCategoryFilter}
                  value={category}
                />

                <Typography>Kategorie</Typography>
                <ToggleButtonGroup
                  exclusive
                  onChange={(event, category) => {
                    if (category !== null)
                      setConfig(prev => ({ ...prev, category }))
                  }}
                  value={category}
                >
                  {[
                    { label: 'Alle', value: LC.NEWSCATEGORY_ALL },
                    {
                      label: LC.NEWSCATEGORY_CONTRACT,
                      value: LC.NEWSCATEGORY_CONTRACT,
                    },
                    {
                      label: LC.NEWSCATEGORY_OBJECT,
                      value: LC.NEWSCATEGORY_OBJECT,
                    },
                  ].map(item => (
                    <ToggleButton key={item.value} value={item.value}>
                      {item.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>

              <Stack
                alignItems={'center'}
                direction={{ lg: 'column', xl: 'row' }}
                spacing={1}
              >
                <Typography>Rubrik</Typography>
                <ToggleButtonGroup
                  exclusive
                  onChange={(event, type) => {
                    if (type !== null) setConfig(prev => ({ ...prev, type }))
                  }}
                  value={type}
                >
                  {[
                    { label: 'Alle', value: LC.NEWSTYPE_ANY },
                    { label: LC.NEWSTYPE_TECH, value: LC.NEWSTYPE_TECH },
                    { label: LC.NEWSTYPE_LEGAL, value: LC.NEWSTYPE_LEGAL },
                    {
                      label: LC.NEWSTYPE_BUSINESS,
                      value: LC.NEWSTYPE_BUSINESS,
                    },
                  ].map(item => (
                    <ToggleButton key={item.value} value={item.value}>
                      {item.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Stack>
            </HStack>

            <NewsSortSelector
              onChange={changeOrderByFilter}
              order={order}
              orderby={orderby}
            />
          </HStack>

          {/* filter */}
          <KTextField
            icon={<SearchTwoTone />}
            onChange={event => {
              const filter = event.target.value

              setConfig(prev => ({ ...prev, filter }))
            }}
            unit={
              filter && (
                <Tooltip title={'Filter löschen'}>
                  <IconButton
                    onClick={() => setConfig(prev => ({ ...prev, filter: '' }))}
                  >
                    <HighlightOffTwoTone />
                  </IconButton>
                </Tooltip>
              )
            }
            value={filter}
          />

          {/* hit hint */}
          <Alert severity={'info'} sx={{ mx: 1 }}>
            <HStack>
              <Typography>
                <strong>
                  {
                    nPool
                      .filter(categoryFilter)
                      .filter(typeFilter)
                      .filter(inputFilter).length
                  }
                </strong>{' '}
                News entsprechen dem Suchfilter{' '}
                <strong>
                  &quot;
                  {filter}
                  &quot;
                </strong>
              </Typography>
            </HStack>
          </Alert>

          {/* newslist */}
          <List
            sx={{
              border: 'solid 1px #CCC',
              m: 1,
              maxHeight: `calc(100vh - 500px)`,
              minHeight: `calc(100vh - 500px)`,
              overflow: 'auto',
              p: 0,
            }}
          >
            <ListSubheader
              sx={{ borderBottom: 'solid 1px #CCC', m: 0, zIndex: 2 }}
            >
              <ListItem sx={{ p: 0 }}>
                <ListItemAvatar>&nbsp;</ListItemAvatar>
                <ListItemText primary={'Name'} sx={{ width: 0.4 }} />
                <ListItemText primary={'ID'} sx={{ width: 0.1 }} />
                <ListItemText primary={'Email'} sx={{ width: 0.3 }} />
                <ListItemText primary={'erstellt'} sx={{ width: 0.1 }} />
                <ListItemText primary={'aktualisiert'} sx={{ width: 0.1 }} />
              </ListItem>
            </ListSubheader>

            {nPool
              .filter(categoryFilter)
              .filter(typeFilter)
              .filter(inputFilter)
              .sort(sortFilter)
              .map(news => (
                <NewsTile key={news.id} news={news} />
              ))}
          </List>
        </Collapse>
      </KPage>
    </>
  )
}
